import { SmartAccountData } from "@/models/smart-account";
export interface OrganisationUnit {
  unitId: string;
  unitName: string;
}

export interface OrganisationView {
  smsReplyTimeWindow: string;
  orgName: string;
  organisationId: string;
  billableUsername: string;
  billableUserId: string;
}

export interface Keyword {
  keywordId?: string;
  keyword?: string;
  status?: string;
}
export class UserAccount {
  username: string | null;
  userId: string;
  active?: boolean;
  status: string;
  admin?: boolean;
  notes?: string | null;
  inheritCredits?: boolean;
  isChecked?: boolean;
  isOwnerUser?: boolean;
  id?: string;
  accessType?: string;
  accessId?: string;
  keyword: string;
  keywords: Keyword[];
  firstName?: string | null;
  lastName?: string | null;
  mobile?: string | null;
  mobileVerified: boolean;
  email?: string | null;
  emailVerified: boolean;
  password?: string;
  organisationView: OrganisationView;
  organisationUnitView: OrganisationUnit;
  roles: string[];
  profanityFilter: boolean;
  colourCode: string;
  firstServiceToLoad?: string;
  defaultMsisdn?: string;
  customerType: string;
  managingOrganisationUnitViews: object[];
  enableTimezone: boolean;
  showTimeText: boolean;
  lastLoginDateTime: string;
  kebabItems: object[];
  dateTimeFormat: string;
  enable24HrFormat: boolean;

  constructor() {
    this.username = null;
    this.userId = "";
    this.active = false;
    this.status = "";
    this.admin = false;
    this.notes = null;
    this.inheritCredits = true;
    this.isChecked = false;
    this.isOwnerUser = false;
    this.id = "";
    this.accessType = "";
    this.keyword = "";
    this.keywords = [];
    this.firstName = null;
    this.lastName = null;
    this.mobile = null;
    this.mobileVerified = false;
    this.email = null;
    this.emailVerified = false;
    this.password = "";
    this.organisationView = {
      smsReplyTimeWindow: "",
      orgName: "",
      organisationId: "",
      billableUsername: "",
      billableUserId: "",
    };
    this.organisationUnitView = {
      unitId: "",
      unitName: "",
    };
    this.roles = [];
    this.profanityFilter = false;
    this.colourCode = "gray-600";
    this.enableTimezone = false;
    this.customerType = "CONNECT";
    this.managingOrganisationUnitViews = [];
    this.showTimeText = false;
    this.lastLoginDateTime = "";
    this.kebabItems = [];
    this.enable24HrFormat = true;
    this.dateTimeFormat = "MMM D YYYY";
  }
}

export class UserAccountDetails {
  content: UserAccount[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;

  constructor() {
    this.content = [];
    this.pageNumber = 0;
    this.pageSize = 25;
    this.totalElements = 0;
  }
}

export interface UserAssignedPerGroup {
  id: string;
  count: number;
}

export interface UserAccessDetails {
  userAccessId?: string;
  userId?: number;
  userAccountsAllowance?: number;
  groupsAllowance?: number;
  msisdnAllowance?: number;
  smartAccountsAllowance?: number;
}

export interface UserContactFieldPreference {
  primary: string;
  secondary: string;
  tertiary: string;
}

export enum COUNT_TYPE {
  SINGLE = "single",
  MULTIPLE = "multiple",
}

export enum OTHER_KEYS {
  ACTIVE = "Active",
  INACTIVE = "Inactive",
  ADMIN = "Admin",
  BILLING = "Billing",
  USER = "User",
  PROVISION = "Processing",
  REJECTED = "Rejected",
  EDIT = "edit",
  DELETE = "delete",
  UPDATE = "update",
  EMAIL = "email",
  MOBILE = "mobile",
  NAMES = "names",
  QUARANT = "Quarant",
}

export enum USER_STATUS_COLOR {
  ACTIVECOLOR = "bg-green-100 text-green-800",
  INACTIVECOLOR = "bg-red-100 text-red-800",
  REJECTEDCOLOR = "bg-amber-100 text-amber-800",
  PROVISIONCOLOR = "bg-yellow-100 text-yellow-800",
}

export enum AccountStatus {
  ACTIVE = "ACTIVE",
  PROVISION = "PROVISION",
  REJECTED = "REJECTED",
  ENABLED = "Enabled",
  DISABLED = "Disabled",
}

export class Banner {
  id: string;
  bannerName: string;
  message: string;
  backgroundColor: string;
  textColor: string;
  icon: string;
  bannerPosition: string;
  textAlignment: string;
  enabled: boolean;
  closable: boolean;

  constructor() {
    this.id = "";
    this.bannerName = "";
    this.message = "";
    this.backgroundColor = "";
    this.textColor = "";
    this.icon = "";
    this.bannerPosition = "";
    this.textAlignment = "";
    this.enabled = true;
    this.closable = false;
  }
}

const getStatus = (
  user: UserAccount | SmartAccountData,
  isText: boolean = true,
) => {
  switch (user.status?.toUpperCase()) {
    case AccountStatus.ACTIVE:
      return isText ? OTHER_KEYS.ACTIVE : USER_STATUS_COLOR.ACTIVECOLOR;
    case AccountStatus.REJECTED:
      return isText ? OTHER_KEYS.REJECTED : USER_STATUS_COLOR.REJECTEDCOLOR;
  }
};

export const getStatusText = (
  user: UserAccount | SmartAccountData,
  isText: boolean = true,
  fromSmartAccount: boolean = false,
) => {
  if (user.userId && user.status?.toUpperCase() == AccountStatus.PROVISION) {
    return isText ? OTHER_KEYS.INACTIVE : USER_STATUS_COLOR.INACTIVECOLOR;
  } else if (user.id && user.status == AccountStatus.PROVISION) {
    return isText ? OTHER_KEYS.PROVISION : "bg-yellow-100 text-yellow-800";
  } else if (
    !fromSmartAccount &&
    !user.userId &&
    user.status != AccountStatus.REJECTED
  ) {
    return isText ? OTHER_KEYS.PROVISION : "bg-yellow-100 text-yellow-800";
  } else {
    return getStatus(user, isText);
  }
};

export const getSMSStatus = (
  status: boolean | undefined,
  isText: boolean = true,
) => {
  const colourClass = status
    ? USER_STATUS_COLOR.ACTIVECOLOR
    : USER_STATUS_COLOR.INACTIVECOLOR;
  const text = status ? AccountStatus.ENABLED : AccountStatus.DISABLED;
  return isText ? text : colourClass;
};

export const getGroupStatus = (status: string | undefined) => {
  if (status?.toUpperCase() == AccountStatus.REJECTED) {
    return OTHER_KEYS.REJECTED;
  } else {
    return OTHER_KEYS.PROVISION;
  }
};
